import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { ExpandableText } from '@components/expandable-text';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { useConditionListValue, useWebhookPayloadVariableGroup } from '@components/integrations';
import { findMappedField } from '@components/integrations/condition';
import {
  IntegrationEndpointVariable,
  WebhookCondition,
} from '@components/integrations/integrations-types';
import { LabelTextGroup } from '@components/label-text-group';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { showError, showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useResubscribeMarketplaceWebhook } from 'api/integrations/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';
import { DescriptionFormField } from 'ui/journey-builder/node-types/common';

type Data = {
  [key: string]: string | SelectOption | SelectOption[];
};

export type WebhookConditionData = { [key: string]: string | VariableValue | VariableValue[] };

const Form = createForm<Data>();

type WebhookDataProps = {
  shortId?: string;
  description?: string;
  uniqueUrl?: string;
  instructions?: string;
  templateId?: string | null;
  payloadVariables?: IntegrationEndpointVariable[];
};

type ConditionComponentProps = {
  description?: string;
  webhookData?: WebhookDataProps;
  initialConditions: WebhookCondition[];
  variableGroups: FieldMappingGroup[];
  defaultData: { [key: string]: VariableValue };
  onSubmit(payload: WebhookConditionData, description: string): Promise<any>;
  onCancel(): void;
  fieldMappingId: string;
  integrationId: string;
  hideFooter?: boolean;
};

export const WebhookConditionComponent = ({
  initialConditions,
  description,
  webhookData,
  variableGroups,
  defaultData,
  onSubmit,
  onCancel,
  fieldMappingId,
  integrationId,
  hideFooter,
}: ConditionComponentProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const [conditions] = useState<WebhookCondition[]>(initialConditions);
  const isDirty = formStore.useIsDirty();

  const payloadVariableGroups = useWebhookPayloadVariableGroup({
    id: webhookData?.shortId ?? '',
    webhook: webhookData,
  });

  const handleSubmit = async (data: Data) => {
    const payload: WebhookConditionData = {};
    conditions.forEach((condition) => {
      const value = data[condition.id];
      if (Array.isArray(value)) {
        payload[condition.id] = value.map((option) => ({ value: option.value }));
      } else if (typeof value === 'object' && 'label' in value) {
        payload[condition.id] = { value: value.value };
      } else {
        payload[condition.id] = value;
      }
    });
    return onSubmit(payload, data.description as string);
  };

  const integrationDetailUrl = generatePath(routePaths.integrationDetail, {
    integrationId: integrationId,
    '*': null,
  });

  const isMarketplace = Boolean(webhookData?.templateId);

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            {webhookData?.description && <ExpandableText text={webhookData.description} />}
            {webhookData?.uniqueUrl && (
              <LabelTextGroup label={t('url')} text={webhookData.uniqueUrl} />
            )}
            {webhookData?.instructions && (
              <LabelTextGroup label={t('instructions')} text={webhookData.instructions} />
            )}
            <DescriptionFormField defaultValue={description} />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('conditions')}>
          <Stack space={4}>
            {conditions.map((condition) => {
              const label =
                (condition.field?.variables && Object.values(condition.field.variables)[0].label) ??
                '';
              const listId = findMappedField(
                condition.field,
                payloadVariableGroups,
              )?.optionsFromListId;
              return listId ? (
                <ConditionListField
                  key={condition.id}
                  condition={condition}
                  label={label}
                  listId={listId}
                  defaultValue={defaultData[condition.id]}
                />
              ) : (
                <Form.FieldMappingTextEditor
                  key={condition.id}
                  id={condition.id}
                  label={label}
                  variableGroups={variableGroups}
                  fieldMappingId={fieldMappingId}
                  defaultValue={defaultData[condition.id] ?? ''}
                  validators={['required']}
                  singleLine
                  plaintext
                />
              );
            })}
            {conditions.length === 0 && (
              <Alert variant="info">
                <Text
                  font="regular14"
                  color="indigo100"
                  text={isMarketplace ? t('conditionsMissingMarketplace') : t('conditionsMissing')}
                />
                {!isMarketplace && (
                  <ActionText.ExternalLink
                    font="regular14"
                    color="indigo100"
                    href={integrationDetailUrl}
                    underline
                  >
                    {t('configureHere')}
                  </ActionText.ExternalLink>
                )}
              </Alert>
            )}
          </Stack>
        </HideContainer>
      </Stack>

      {!hideFooter && (
        <Sidebar.Footer>
          <ButtonGroup align="right">
            <Button variant="secondary" text={t('core.cancel')} onClick={onCancel} />
            <Form.Submit variant="primary-blue" text={t('core.save')} disabled={!isDirty} />
          </ButtonGroup>
        </Sidebar.Footer>
      )}
    </FormWithUnsavedChangesPrompt>
  );
};

type ConditionListFieldProps = {
  condition: WebhookCondition;
  label: string;
  listId: string;
  defaultValue?: VariableValue;
};

const ConditionListField = ({
  condition,
  label,
  listId,
  defaultValue,
}: ConditionListFieldProps) => {
  const { options, listKey, reconciledValue, isMulti, staleError } = useConditionListValue({
    listId,
    value: defaultValue,
    comparator: condition.comparator,
  });

  return (
    <Stack space={2}>
      {isMulti ? (
        <Form.SelectMulti
          key={listKey}
          // @ts-ignore
          id={condition.id}
          label={label}
          options={options}
          defaultValue={reconciledValue.value as SelectOption[]}
          validators={['required']}
        />
      ) : (
        <Form.Select
          key={listKey}
          selection="full"
          id={condition.id}
          label={label}
          options={options}
          defaultValue={reconciledValue.value as SelectOption}
          validators={['required']}
          isClearable={false}
        />
      )}
      {staleError}
    </Stack>
  );
};

export const WebhookNotSubscribedSection = ({ webhookShortId }: { webhookShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const subscribeMutation = useResubscribeMarketplaceWebhook(webhookShortId);
  const { canManageIntegrations } = usePermissions();

  const handleSubscribe = async () => {
    try {
      const result = await subscribeMutation.mutateAsync();
      if (result.success) {
        showSuccess(t('subscribeSuccess'));
      } else {
        showError(t('subscribeError'));
      }
    } catch {
      showError(t('subscribeError'));
    }
  };

  const renderSubscribeButton = () => {
    if (!canManageIntegrations) {
      return (
        <Button
          variant="primary-blue"
          text={t('subscribe')}
          disabled
          tooltip={t('noPermissionResubscribe')}
        />
      );
    }
    return <Button variant="primary-blue" text={t('subscribe')} onClick={handleSubscribe} />;
  };

  return (
    <Card variant="orange" spaceX={3} spaceY={4} hasBorder={false}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={4} orientation="horizontal">
          <IconFormIndicatorWarning />
          <Text text={t('webhookNotSubscribed')} color="textSecondary" font="regular14" />
        </Stack>
        {renderSubscribeButton()}
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    actionDetails: 'Action details',
    conditions: 'Conditions',
    conditionsMissing:
      'This webhook has no conditons configured. In order to use this action you need to go to the webhook settings and add them.',
    conditionsMissingMarketplace:
      'This webhook has no conditions configured. Please contact support.',
    configureHere: 'Configure here',
    webhookNotSubscribed: 'Webhook subscription failed',
    subscribe: 'Subscribe',
    subscribeSuccess: 'Webhook subscribed successfully',
    subscribeError: 'Failed to subscribe webhook. Please contact support.',
    noPermissionResubscribe: 'Only Admins & Editors can resubscribe webhooks',
    url: 'URL',
    instructions: 'Instructions',
  },
};
